import { AddOn, CartProduct, Recurrence, ShoppingCart, ShoppingCartAddonsInput } from '@buy-viasat/types/build/bv';
import api, { apiEndpoints } from 'shared/services/axios';

// REST response types - the actual structure returned by the upsertCartAddons endpoint

enum RestItemKind {
  PROMO = 'PROMO',
}

type RestCartItemPrice = {
  kind?: string;
  recurrence?: string;
  amount?: { value: number };
  name: string;
};

type RestPromo = {
  duration: number | null;
  price: number | null;
};

type RestCartItem = {
  productCandidateId: string;
  productTypeId: string;
  kind: string;
  name: string;
  description: string;
  prices: RestCartItemPrice[];
  promo?: RestPromo;
  contractTerms?: string;
  contractTermsDisclosure?: string;
  leaseFeeShortName?: string;
  leaseFeePrice?: number;
  characteristics?: Record<string, string | number | null>;
  linkedProductName?: string;
  linkedProductCandidateId?: string;
};

type RestCartProductType = {
  id: string;
  name: string;
  description: string | null;
  kind: string | null;
  offerId: string;
  characteristics?: Record<string, string | number | null>;
  promo?: RestPromo;
};

// Bundled addons returned by the new BE include characteristics parsed from marketing_copy,
// which may contain hideCartItem (equivalent to HIDE_CART_ITEM on the old BE).
type RestBundledAddonCharacteristics = Record<string, string | number | null> & {
  offerDescription?: string;
  attribute1?: string;
  attribute2?: string;
  attribute3?: string;
  attribute4?: string;
  attribute5?: string;
  crossOut01?: string;
  crossOut02?: string;
};

type RestBundledAddon = {
  id: string;
  name: string;
  kind: string;
  characteristics?: RestBundledAddonCharacteristics;
};

type RestProductTypes = {
  plan: RestCartProductType;
  addons: RestCartProductType[];
  bundledAddons: RestBundledAddon[];
  closingOffer: RestCartProductType | null;
};

type RestMonthlyPrices = {
  totalWithPromos: number;
  totalWithoutPromos: number;
  lease: number;
};

type RestOneTimePrices = {
  totalWithPromos: number;
  totalWithoutPromos: number;
  lease: number;
};

type RestPrices = {
  monthly: RestMonthlyPrices;
  oneTime: RestOneTimePrices;
};

type RestUpfrontFee = {
  name: string;
  fee: number;
  discount: number | null;
  total: number;
  kind: string;
};

type RestDroppedPortfolioItem = {
  name: string;
  productTypeId: string;
};

export type RestShoppingCart = {
  id: string;
  status: string;
  cartItems: RestCartItem[];
  productTypes: RestProductTypes | null;
  prices: RestPrices;
  upfrontFees: RestUpfrontFee[];
  droppedPortfolioItems: RestDroppedPortfolioItem[];
};

function getPreferredOrFirstAmount(prices: RestCartItem['prices']): number | null {
  const monthlyPrice = prices.find(
    (priceEntry) =>
      priceEntry.recurrence?.toUpperCase() === Recurrence.MONTHLY ||
      priceEntry.kind?.toUpperCase() === Recurrence.MONTHLY,
  );

  if (monthlyPrice?.amount?.value != null) {
    return monthlyPrice.amount.value;
  }

  if (prices[0]?.amount?.value != null) {
    return prices[0].amount.value;
  }

  return null;
}

function getItemPrice(cartItems: RestCartItem[], productTypeId: string): number | null {
  const matchedItem = cartItems.find((cartItem) => cartItem.productTypeId === productTypeId);
  if (!matchedItem) {
    return null;
  }

  return getPreferredOrFirstAmount(matchedItem.prices);
}

function getPromoDuration(characteristics?: Record<string, string | number | null>): number {
  if (!characteristics) {
    return 0;
  }

  const possibleKeys = ['duration', 'promoDuration', 'discountDuration'];
  for (const key of possibleKeys) {
    const value = characteristics[key];
    if (typeof value === 'number') {
      return value;
    }
    if (typeof value === 'string' && value.trim() !== '' && !Number.isNaN(Number(value))) {
      return Number(value);
    }
  }

  return 0;
}

function normalizePromoPrice(rawPromoPrice: number): number {
  return rawPromoPrice > 0 ? -rawPromoPrice : rawPromoPrice;
}

function getExplicitConsent(productTypeCharacteristics: Record<string, string | number | null>): boolean {
  const { explicitConsentRequired } = productTypeCharacteristics;

  return typeof explicitConsentRequired === 'string' && explicitConsentRequired.toUpperCase() === 'YES';
}

function getMonthlyAmount(prices: RestCartItem['prices']): number | null {
  return getPreferredOrFirstAmount(prices);
}

function buildPromoByLinkedCandidateId(cartItems: RestCartItem[]): Map<string, { duration: number; price: number }> {
  const promoByCandidateId = new Map<string, { duration: number; price: number }>();

  cartItems.forEach((cartItem) => {
    if (!cartItem.linkedProductCandidateId) {
      return;
    }

    const isPromoItem = cartItem.kind?.toUpperCase().includes(RestItemKind.PROMO);
    if (!isPromoItem) {
      return;
    }

    const amount = getMonthlyAmount(cartItem.prices);
    if (amount == null) {
      return;
    }

    promoByCandidateId.set(cartItem.linkedProductCandidateId, {
      duration: getPromoDuration(cartItem.characteristics),
      price: normalizePromoPrice(amount),
    });
  });

  return promoByCandidateId;
}

function createCartProduct(
  productType: RestCartProductType,
  cartItems: RestCartItem[],
  promoByCandidateId?: Map<string, { duration: number; price: number }>,
): CartProduct {
  const matchedItem = cartItems.find((cartItem) => cartItem.productTypeId === productType.id);
  const linkedPromo = matchedItem?.productCandidateId
    ? (promoByCandidateId?.get(matchedItem.productCandidateId) ?? null)
    : null;
  const rawPromo =
    matchedItem?.promo ??
    (linkedPromo ? { duration: linkedPromo.duration, price: linkedPromo.price } : null) ??
    productType.promo ??
    null;
  const promo =
    rawPromo && rawPromo.price != null
      ? { duration: rawPromo.duration ?? 0, price: normalizePromoPrice(rawPromo.price) }
      : null;

  return {
    name: productType.name,
    productTypeId: productType.id,
    productCandidateId: matchedItem?.productCandidateId ?? null,
    price: getItemPrice(cartItems, productType.id),
    categoryName: null,
    promo,
    leaseFeeShortName: matchedItem?.leaseFeeShortName ?? null,
    leaseFeePrice: matchedItem?.leaseFeePrice ?? null,
    contractTerms: matchedItem?.contractTerms ?? null,
    contractTermsDisclosure: matchedItem?.contractTermsDisclosure ?? null,
    accountSetupFeeShortName: null,
    accountSetupFeePrice: null,
    displayOrder: null,
    explicitConsent: productType.characteristics ? getExplicitConsent(productType.characteristics) : null,
    isBundle: null,
    shouldHideBundleAddon: null,
  };
}

export function mapRestCartToGqlCart(restCart: RestShoppingCart): ShoppingCart {
  const flatItems = restCart.cartItems ?? [];
  const productTypes = restCart.productTypes;
  const promoByCandidateId = buildPromoByLinkedCandidateId(flatItems);

  const plan = productTypes?.plan ? createCartProduct(productTypes.plan, flatItems, promoByCandidateId) : null;

  const allBundledAddons = productTypes?.bundledAddons ?? [];

  // Mirror old BE behaviour: all bundled addons go into cartItems.addons with isBundle: true.
  // shouldHideBundleAddon is derived from the hideCartItem characteristic (camelCase of
  // HIDE_CART_ITEM), which the new BE sets on the bundled addon's characteristics when an
  // upgraded version of the same addon is selected — exactly like PAL's HIDE_CART_ITEM flag.
  const bundledAddonProducts: CartProduct[] = allBundledAddons.map((addon) => ({
    ...createCartProduct(
      {
        id: addon.id,
        name: addon.name,
        kind: addon.kind,
        characteristics: addon.characteristics,
        description: null,
        offerId: '',
      },
      flatItems,
    ),
    isBundle: true,
    shouldHideBundleAddon: addon.characteristics?.hideCartItem === '1',
  }));

  const addons = [
    ...(productTypes?.addons ?? []).map((addonProductType) => createCartProduct(addonProductType, flatItems)),
    ...bundledAddonProducts,
  ];

  // Keep bundledAddons on ShoppingCart (used by the Plans page via setAvailableAddons).
  const bundledAddons: AddOn[] = allBundledAddons.map(({ id, name, kind, characteristics = {} }) => {
    const {
      offerDescription = null,
      attribute1 = '',
      attribute2 = '',
      attribute3 = '',
      attribute4 = '',
      attribute5 = '',
      crossOut01 = '',
      crossOut02 = '',
    } = characteristics;

    return {
      id,
      name,
      kind,
      description: offerDescription,
      attributes: [attribute1, attribute2, attribute3, attribute4, attribute5],
      crossOuts: [crossOut01, crossOut02],
      price: getItemPrice(flatItems, id) ?? 0,
      promo: { duration: 0, price: 0 },
      isBundle: true,
    };
  });

  const closingOffers = productTypes?.closingOffer ? [createCartProduct(productTypes.closingOffer, flatItems)] : [];

  const monthlyPromo = restCart.prices.monthly.totalWithPromos - restCart.prices.monthly.totalWithoutPromos;
  const oneTimePromo = restCart.prices.oneTime.totalWithPromos - restCart.prices.oneTime.totalWithoutPromos;
  const feesAndDiscounts = (restCart.upfrontFees ?? []).map((upfrontFee) => ({
    name: upfrontFee.name,
    fee: upfrontFee.fee,
    discount: upfrontFee.discount,
    total: upfrontFee.total,
    kind: upfrontFee.kind,
  }));

  //TODO - remove leaseFeeShortName altogether from the repo after incremental deployment - so as to not break backwards compatibility
  return {
    id: restCart.id,
    status: restCart.status,
    leaseFeeShortName: '',
    bundledAddons,
    cartItems: {
      plan,
      addons,
      closingOffers,
      fiberDiscounts: null,
    },
    prices: {
      monthly: {
        totalWithPromos: restCart.prices.monthly.totalWithPromos,
        totalWithoutPromos: restCart.prices.monthly.totalWithoutPromos,
        lineItems: {
          promo: monthlyPromo,
          lease: restCart.prices.monthly.lease,
        },
      },
      oneTime: {
        totalWithPromos: restCart.prices.oneTime.totalWithPromos,
        totalWithoutPromos: restCart.prices.oneTime.totalWithoutPromos,
        lease: restCart.prices.oneTime.lease,
        lineItems: {
          promo: oneTimePromo,
        },
        feesAndDiscounts,
      },
    },
  };
}

export async function upsertCartAddonsRest(input: ShoppingCartAddonsInput): Promise<ShoppingCart> {
  const response = await api.post<RestShoppingCart>(apiEndpoints.upsertCartAddons, input);
  return mapRestCartToGqlCart(response.data);
}
